// Plain-English takeaways computed from the rows a widget already loaded — no AI, no extra query.
import { formatValue, formatNumber, labelsFromMeta } from "@/lib/chartTheme";

export const INSIGHT_TYPES = [
  "bar", "bar_stacked", "line", "area", "combo", "pie", "donut",
  "funnel", "treemap", "radar", "heatmap", "gauge", "geo_map", "bubble_map",
];

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : null; };
const pctOf = (x) => { const p = Math.abs(x) * 100; return `${p >= 10 || p === 0 ? Math.round(p) : p.toFixed(1)}%`; };
const dirOf = (x) => (x >= 0 ? "up" : "down");

function groupSum(rows, keyField, valField) {
  const m = new Map();
  rows.forEach((r) => {
    const v = num(r[valField]);
    if (v === null) return;
    const k = String(r[keyField] ?? "\u2014");
    m.set(k, (m.get(k) || 0) + v);
  });
  return m;
}

function categoryLines({ cats, total, allPos, fv, mLabel }) {
  const lines = [];
  const sorted = [...cats].sort((a, b) => b[1] - a[1]);
  const [topName, topVal] = sorted[0];
  const share = allPos && total > 0 && cats.length > 1 ? ` \u2014 ${pctOf(topVal / total)} of the ${fv(total)} total` : "";
  lines.push(`${topName} leads on ${mLabel} with ${fv(topVal)}${share}.`);
  if (cats.length >= 5 && allPos && total > 0) {
    const top3 = sorted.slice(0, 3).reduce((s, [, v]) => s + v, 0);
    lines.push(`The top 3 of ${cats.length} account for ${pctOf(top3 / total)} of the total.`);
  }
  const mean = total / cats.length;
  const sd = Math.sqrt(cats.reduce((s, [, v]) => s + (v - mean) ** 2, 0) / cats.length);
  if (cats.length >= 5 && sd > 0 && mean > 0 && topVal > mean + 2 * sd) {
    lines.push(`${topName} stands out at ${(topVal / mean).toFixed(1)}\u00D7 the average of ${fv(mean)}.`);
  } else if (cats.length >= 3) {
    const [lowName, lowVal] = sorted[sorted.length - 1];
    lines.push(`${lowName} is lowest at ${fv(lowVal)}.`);
  }
  return lines;
}

function timeLines({ cats, fv }) {
  const lines = [];
  const n = cats.length;
  const [lastName, lastVal] = cats[n - 1];
  const [prevName, prevVal] = cats[n - 2];
  if (prevVal) {
    const ch = (lastVal - prevVal) / Math.abs(prevVal);
    lines.push(`Latest ${lastName} is ${fv(lastVal)} \u2014 ${dirOf(ch)} ${pctOf(ch)} vs ${prevName}.`);
  } else {
    lines.push(`Latest ${lastName} is ${fv(lastVal)}.`);
  }
  if (n >= 3) {
    const [firstName, firstVal] = cats[0];
    if (firstVal) {
      const ch = (lastVal - firstVal) / Math.abs(firstVal);
      lines.push(`${dirOf(ch) === "up" ? "Up" : "Down"} ${pctOf(ch)} overall from ${firstName} to ${lastName}.`);
    }
  }
  const peak = [...cats].sort((a, b) => b[1] - a[1])[0];
  if (n >= 4 && peak[0] !== lastName) lines.push(`Peak was ${peak[0]} at ${fv(peak[1])}.`);
  return lines;
}

// Strongest/weakest group when a Row (series) field splits the chart.
function seriesLine({ rows, seriesKey, mk, fv }) {
  const m = groupSum(rows, seriesKey, mk);
  if (m.size < 2) return null;
  const sorted = [...m].sort((a, b) => b[1] - a[1]);
  const total = sorted.reduce((s, [, v]) => s + v, 0);
  const share = total > 0 ? ` (${pctOf(sorted[0][1] / total)})` : "";
  return `Within that, ${sorted[0][0]} is the largest at ${fv(sorted[0][1])}${share}; ${sorted[sorted.length - 1][0]} the smallest at ${fv(sorted[sorted.length - 1][1])}.`;
}

// Which group moved most between the last two periods.
function moverLine({ rows, xKey, seriesKey, mk, cats, fv }) {
  const lastCat = cats[cats.length - 1][0], prevCat = cats[cats.length - 2][0];
  const at = (cat) => groupSum(rows.filter((r) => String(r[xKey] ?? "\u2014") === cat), seriesKey, mk);
  const prev = at(prevCat), last = at(lastCat);
  let best = null;
  last.forEach((v, s) => {
    const p = prev.get(s);
    if (!p) return;
    const ch = (v - p) / Math.abs(p);
    if (!best || Math.abs(ch) > Math.abs(best.ch)) best = { s, ch };
  });
  return best ? `Biggest mover: ${best.s} ${dirOf(best.ch)} ${pctOf(best.ch)} in ${lastCat}.` : null;
}

function heatLines({ rows, xKey, seriesKey, mk, fv }) {
  const cells = rows
    .map((r) => ({ x: String(r[xKey] ?? "\u2014"), y: String(r[seriesKey] ?? "\u2014"), v: num(r[mk]) }))
    .filter((c) => c.v !== null);
  if (cells.length < 2) return [];
  const sorted = [...cells].sort((a, b) => b.v - a.v);
  const hi = sorted[0], lo = sorted[sorted.length - 1];
  const lines = [
    `Hottest cell is ${hi.y} \u00D7 ${hi.x} at ${fv(hi.v)}.`,
    `Coolest is ${lo.y} \u00D7 ${lo.x} at ${fv(lo.v)}.`,
  ];
  const byRow = [...groupSum(cells, "y", "v")];
  if (byRow.length > 1) {
    const total = byRow.reduce((s, [, v]) => s + v, 0);
    const top = byRow.sort((a, b) => b[1] - a[1])[0];
    lines.push(total > 0 ? `${top[0]} is the strongest row overall \u2014 ${pctOf(top[1] / total)} of everything shown.` : `${top[0]} is the strongest row overall.`);
  }
  return lines;
}

function gaugeLines({ rows, mk, yKeys, fv, options, mLabel }) {
  const v = num(rows[0] && rows[0][mk]);
  if (v === null) return [];
  const fromMeasure = yKeys[1] != null ? num(rows[0][yKeys[1]]) : null;
  const t = options.gaugeTarget !== undefined && options.gaugeTarget !== "" ? num(options.gaugeTarget) : fromMeasure;
  if (!t) return [`${mLabel} is at ${fv(v)}.`];
  const diff = v - t;
  return [`${fv(v)} is ${pctOf(v / t)} of the ${fv(t)} target \u2014 ${fv(Math.abs(diff))} ${diff >= 0 ? "ahead" : "short"}.`];
}

export function buildInsights({ type, data, spec = {}, options = {}, isTime = false, max = 3 }) {
  const rows = (data && data.rows) || [];
  if (!INSIGHT_TYPES.includes(type) || !rows.length) return [];
  const cols = data.columns || [];
  const xKey = spec.x_axis || cols[0];
  const yKeys = (spec.y_axis && spec.y_axis.length ? spec.y_axis : cols.slice(1)).filter(Boolean);
  const mk = yKeys[0];
  if (!mk || !xKey) return [];

  const fmtByKey = {};
  (((data.meta) || {}).measures || []).forEach((m) => { if (m.key) fmtByKey[m.key] = m.format; });
  const loc = options.locale || undefined;
  const fv = (v, key = mk) => (fmtByKey[key] ? formatValue(v, fmtByKey[key], loc) : formatNumber(v, loc));
  const labelByKey = labelsFromMeta(data);
  const mLabel = (options.seriesLabels || {})[mk] || labelByKey[mk] || mk;

  if (type === "gauge") return gaugeLines({ rows, mk, yKeys, fv, options, mLabel }).slice(0, max);
  const seriesKey = spec.series && spec.series !== xKey ? spec.series : null;
  if (type === "heatmap" && seriesKey) return heatLines({ rows, xKey, seriesKey, mk, fv }).slice(0, max);

  const cats = [...groupSum(rows, xKey, mk)];
  if (cats.length < 2) return [];
  const total = cats.reduce((s, [, v]) => s + v, 0);
  const allPos = cats.every(([, v]) => v >= 0);

  const lines = isTime ? timeLines({ cats, fv }) : categoryLines({ cats, total, allPos, fv, mLabel });
  if (seriesKey) {
    const extra = isTime ? moverLine({ rows, xKey, seriesKey, mk, cats, fv }) : seriesLine({ rows, seriesKey, mk, fv });
    if (extra) lines.splice(1, 0, extra);
  }
  return lines.filter(Boolean).slice(0, max);
}
